import * as React from 'react';
import { DateTimeFormatter, LocalDate } from 'js-joda';
import FocusTrap from 'focus-trap-react';
import clsx from 'clsx';

import * as globalEventsHelper from '@tock/shared/utils/globalEventsHelper';
import { DateAvailabilityObject } from 'types';
import { DisplayDate } from '@tock/time';
import { IWithContextProps, withContext } from '@tock/coretypes';
import { IconChevronDown } from '@tock/icons';
import { Typography } from 'omakase';
import { css } from '@emotion/react';
import { preventBubbleOutside } from '@tock/shared/utils/eventUtils';
import { uniqueId } from 'es-toolkit/compat';
import BaseComponent from '@tock/shared/components/BaseComponent';
import ConsumerCalendar from 'components/calendar/ConsumerCalendar';

interface Props {
  availableDates?: DateAvailabilityObject;
  dateClassName: string;
  hasExpandedCalendar?: boolean;
  externalCalendarIsExpanded?: boolean;
  hasInitialFocus?: boolean;
  lockedDates: DateAvailabilityObject;
  offeredDates?: DateAvailabilityObject;
  onSelectDate(date: string): void;
  onToggleExpandedCalendar?(event: React.MouseEvent<HTMLElement>): void;
  openDates: DateAvailabilityObject;
  selectedDate: LocalDate;
  soldDates?: DateAvailabilityObject;
}

interface State {
  showDateSelector: boolean;
  dateSelectorId: string;
}

const ariaDateFormatter = DateTimeFormatter.ofPattern('M/d/yyyy');

/**
 * Shows a selector for the desired reservation date, and, optionally,
 * internally manages a calendar popup for selecting the date.
 *
 * If both this.props.hasExpandedCalendar and this.props.onToggleExpandedCalendar are provided,
 * the calendar popup will not be managed internally, and instead the parent component is
 * expected to manage presentation of whatever calendar components are on the page in the
 * way it desires.
 *
 * Otherwise, the component internally manages a calendar popup. This includes
 * trapping focus within the calendar popup when it is open, and responding to escape
 * to close it.
 */
class SearchBarDateSection extends BaseComponent<Props & IWithContextProps, State> {
  selectDateRef: React.RefObject<HTMLButtonElement>;
  dateSelectorRef: React.RefObject<HTMLDivElement>;
  isUnmounting = false;

  constructor(props: Props & IWithContextProps) {
    super(props);
    this.state = {
      showDateSelector: false,
      dateSelectorId: uniqueId('date_selector_'),
    };
    this.selectDateRef = React.createRef();
    this.dateSelectorRef = React.createRef();
  }

  consumerCalendarIsExternallyManaged() {
    return (
      !this.state.showDateSelector &&
      this.props.hasExpandedCalendar &&
      !!this.props.onToggleExpandedCalendar
    );
  }

  handleToggleDateSelector(event: React.MouseEvent<HTMLElement>) {
    const { showDateSelector } = this.state;

    if (showDateSelector) {
      this.handleHideDateSelector();
    } else if (this.consumerCalendarIsExternallyManaged()) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      this.props.onToggleExpandedCalendar!(event);
    } else {
      event.nativeEvent.stopImmediatePropagation();
      if (!this.shouldTrapDateSelectorFocus()) {
        globalEventsHelper.addGlobalClickEvent(this.handleHideDateSelector, 'popover');
      }
      this.setState({
        showDateSelector: true, // if we got this far, we know that showDateSelector did not start out as false
      });
    }
  }

  handleHideDateSelector() {
    globalEventsHelper.removeGlobalClickEvent(this.handleHideDateSelector, 'popover');
    if (this.isUnmounting) {
      return;
    }
    this.setState({
      showDateSelector: false,
    });
  }

  componentWillUnmount() {
    this.isUnmounting = true;
    globalEventsHelper.removeGlobalClickEvent(this.handleHideDateSelector, 'popover');
  }

  shouldTrapDateSelectorFocus() {
    return !this.consumerCalendarIsExternallyManaged();
  }

  handleSelectDate(date: string) {
    this.handleHideDateSelector();
    this.props.onSelectDate(date);
  }

  // We do the focus-related componentDidMount only in a setTimeout after 600 ms,
  // because this content may be presented in an iframe. If it is, and:
  //
  // * If voiceover is turned on, and the user is using Safari, some kind of
  // setTimeout delay is necessary to avoid a problem where the focus gets trapped
  // outside the dialog. Any amount of delay seems to be sufficient here.
  //
  // * If voiceover is turned on, and the user is using Chrome, focusing one of
  // these buttons too soon causes an issue where navigating the page content
  // will loop infinitely within a small part of the page instead of including
  // all pageable elements. For this problem, a small delay is not enough;
  // experimentally, 600 ms is consistently sufficient.
  componentDidMount() {
    const { hasInitialFocus } = this.props;
    window.setTimeout(() => {
      if (hasInitialFocus) {
        this.selectDateRef.current?.focus();
      }
    }, 600);
  }

  render() {
    const {
      availableDates,
      externalCalendarIsExpanded,
      lockedDates,
      dateClassName,
      offeredDates,
      openDates,
      selectedDate,
      soldDates,
    } = this.props;
    const { showDateSelector, dateSelectorId } = this.state;
    // Check showDateSelector before building calendarPopupBody as a small optimization,
    // even though we recheck it below.
    const calendarPopupBody = showDateSelector && (
      <div
        id={dateSelectorId}
        role="group"
        aria-label="Choose reservation date"
        data-testid="reservation-search-calendar"
        className="SearchBarDropdown"
        onClick={preventBubbleOutside}
        ref={this.dateSelectorRef}
      >
        <ConsumerCalendar
          availableDates={availableDates}
          autoFocusOnMount
          lockedDates={lockedDates}
          offeredDates={offeredDates}
          openDates={openDates}
          selectedDate={selectedDate ? selectedDate.toString() : undefined}
          soldDates={soldDates}
          wideView={false}
          onSelect={this.handleSelectDate}
        />
      </div>
    );

    return (
      <div
        className={clsx(dateClassName, {
          'is-managing-popup': showDateSelector,
        })}
      >
        <button
          className="SearchBar-sectionDropdown SearchBar-sectionDropdown--sakePadding"
          aria-expanded={
            this.consumerCalendarIsExternallyManaged()
              ? externalCalendarIsExpanded
              : showDateSelector
          }
          // We could do better in assigning aria-controls here, but have not been assigning a meaningful id in the
          // consumerCalendarIsExternallyManaged() case for a while.
          aria-controls={this.consumerCalendarIsExternallyManaged() ? undefined : dateSelectorId}
          aria-label={
            selectedDate
              ? `Desired reservation date, current selection is ${selectedDate.format(
                  ariaDateFormatter
                )}`
              : 'Desired reservation date'
          }
          title="Desired reservation date"
          onClick={this.handleToggleDateSelector}
          data-testid="reservation-date-button"
          ref={this.selectDateRef}
        >
          <div className="SearchBar-sectionDropdownContent">
            <div
              css={css`
                align-items: flex-start;
                display: flex;
                flex-direction: column;
              `}
            >
              <Typography variant="caption" color="fgSubtle">
                Date
              </Typography>
              <Typography variant="body02" color="fgDefault">
                {selectedDate ? (
                  <DisplayDate dateValue={selectedDate} displayMonthAs="short" />
                ) : (
                  'Select date'
                )}
              </Typography>
            </div>
            <IconChevronDown className="SearchBar-sectionDropdownIcon" />
          </div>
        </button>

        {showDateSelector &&
          (this.shouldTrapDateSelectorFocus() ? (
            <FocusTrap
              focusTrapOptions={{
                clickOutsideDeactivates: true,
                setReturnFocus: () => this.selectDateRef.current ?? false,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                fallbackFocus: () => this.dateSelectorRef.current!,
                onDeactivate: this.handleHideDateSelector,
              }}
            >
              {calendarPopupBody}
            </FocusTrap>
          ) : (
            calendarPopupBody
          ))}
      </div>
    );
  }
}

export default withContext(SearchBarDateSection);
